// @ts-strict-ignore
import ExperimentContext from '@carvana/experiment/ExperimentProviderContext';
import { useCallback, useContext } from 'react';
import {
  recordActionClicked as recordClick,
  recordEventAndNavigate as recordAndNavigate,
  recordGenericPageEvent as recordPageEvent,
  recordGenericEvent as recordEvent,
  recordEventAndNavigateTo as recordAndNavigateTo,
  recordEventAndNavigateInNewWindow as recordAndNavigateInNewWindow,
  recordMoMetricsEvent as recordMoMetrics
} from '../analytics/uiMetrics';
import type { EventActions, EventSourceContext } from '../analytics/uiMetrics';
import { getIOSInstallationID } from '../shared/urlParams';

const useAnalyticEvent = () => {
  const { experiments } = useContext(ExperimentContext);

  const recordActionClicked = useCallback(
    (pageName: string, component: string, eventClickedData = {}, sendImmediately?: boolean) => {
      const iosInstallationID = getIOSInstallationID();
      const customAttributes = {
        ...eventClickedData,
        experiments,
        ...iosInstallationID
      };
      recordClick(pageName, component, customAttributes, sendImmediately);
    },
    [experiments]
  );

  const recordEventAndNavigate = useCallback(
    (
      event: React.MouseEvent<HTMLAnchorElement>,
      pageName: string,
      component: string,
      eventClickedData = {}
    ) => {
      const iosInstallationID = getIOSInstallationID();
      const customAttributes = {
        ...eventClickedData,
        experiments,
        ...iosInstallationID
      };
      recordAndNavigate(event, pageName, component, customAttributes);
    },
    [experiments]
  );

  const recordEventAndNavigateTo = useCallback(
    (destination: string, pageName: string, component: string, eventClickedData = {}) => {
      const iosInstallationID = getIOSInstallationID();
      const customAttributes = {
        ...eventClickedData,
        experiments,
        ...iosInstallationID
      };
      recordAndNavigateTo(destination, pageName, component, customAttributes);
    },
    [experiments]
  );

  const recordEventAndNavigateInNewWindow = useCallback(
    (destination: string, pageName: string, component: string, eventClickedData = {}) => {
      const iosInstallationID = getIOSInstallationID();
      const customAttributes = {
        ...eventClickedData,
        experiments,
        ...iosInstallationID
      };
      recordAndNavigateInNewWindow(destination, pageName, component, customAttributes);
    },
    [experiments]
  );

  const recordGenericPageEvent = useCallback(
    (
      pageName: string,
      component: string,
      eventAction: EventActions,
      eventClickedData = {},
      sendImmediately = false
    ) => {
      const iosInstallationID = getIOSInstallationID();
      const customAttributes = {
        ...eventClickedData,
        experiments,
        ...iosInstallationID
      };
      recordPageEvent(pageName, component, eventAction, customAttributes, sendImmediately);
    },
    [experiments]
  );

  const recordGenericEvent = useCallback(
    (
      eventName: string,
      eventContext = {},
      sendImmediately = false
    ) => {
      const iosInstallationID = getIOSInstallationID();
      const fullContext = {
        ...eventContext,
        ...iosInstallationID
      };
      recordEvent(eventName, fullContext, sendImmediately);
    },
    []
  );

  const recordMoMetricsEvent = useCallback(
    (eventSourceContext: EventSourceContext, customAttributes = {}, sendImmediately = false) => {
      const iosInstallationID = getIOSInstallationID();
      const enhancedCustomAttributes = { ...customAttributes, ...iosInstallationID };

      return recordMoMetrics(eventSourceContext, enhancedCustomAttributes, sendImmediately);
    },
    []
  );

  return {
    recordActionClicked,
    recordEventAndNavigate,
    recordGenericPageEvent,
    recordGenericEvent,
    recordEventAndNavigateTo,
    recordEventAndNavigateInNewWindow,
    recordMoMetricsEvent
  };
};

export enum PageName {
  Marketing = 'MarketingPage'
}

export enum ContainerApps {
  VALUE_TRACKER = 'VALUE_TRACKER'
}

export default useAnalyticEvent;
